// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { Helmet } from "react-helmet";
import { isEmpty } from "lodash";

// ANT COMPONENTS
import { Col, Row } from "antd";
import { Button, Tabs, Pagination } from "@life-line-lab/components-react";
import {} from "@ant-design/icons";
import {
  RightCircleOutlined,
  ShoppingCartOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";

// INTERNAL COMPONENTS
import CustomerProfileListComponent from "Components/customer-management/customer-profile-list";
import CustomerProfileDetailsComponent from "Components/customer-management/customer-profile-details";

// HOOKS
import useQuery from "Hooks/query.hook";
import { useAccessControlList, useItemsPerPage, useSession } from "Hooks/index";
import usePagination from "Hooks/pagination.hook";

// HELPERS
import { decode, encode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";

// CONSTANT
import { DEFAULT_MAX_PER_PAGE } from "Components/shared/constants";

// TYPES
import { OrgList, Sorting, SortingMode } from "../../../types/common.types";
import { CustomerApprovalStatus, OrgType } from "../../../types/customer-form.types";

// SERVICES
import {
  customerApproval,
  CustomerApprovalInput,
  deleteCustomer,
  fetchBearerToken,
  fetchCustomer,
  fetchCustomerList,
  fetchCustomerListCount,
  restoreCustomer,
} from "Services/customer.service";
import { getCompanyList, getLogisticProviderList } from "Services/account.services";
import { updateOperationalHoursForIndividuals } from "Services/location.service";

// ATOM
import { customerAtom, CustomerAtomType } from "Recoil/customer.atom";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";

const toNum = (data) => {
  if (typeof data === "string") {
    if (data.match(/^[0-9]+$/)) {
      return parseInt(data);
    } else {
      return 1;
    }
  }
  return data;
};

const CustomerProfilePage = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const query = useQuery();
  const { org, roleTypes } = useSession();

  const tabKey = query.get("tabKey") || "0";
  const page = query.get("page") || 1;
  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
  });
  const initialQueryParams = { page, offset: (toNum(page) - 1) * toNum(itemPerPage) };
  const initialSorting = { sortBy: "name", mode: SortingMode.DESC };
  let initialFilters = {};

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Customer_Management",
  });

  const isReadonly = !permission.rights.includes("CREATE") && !permission.rights.includes("UPDATE");
  const canViewList = permission.rights.includes("LIST");
  const canFetchCount = permission.rights.includes("FETCH_COUNT");
  const canView = permission.rights.includes("VIEW");
  const canUpdate = permission.rights.includes("UPDATE");
  const canDelete = permission.rights.includes("DELETE");
  const showCompanyColumn = permission.rights.includes("SHOW_COMPANY_COLUMN");
  const showCompanyListFilter = permission.rights.includes("COMPANY_LIST_FILTER");
  const showLogisticProviderListFilter = permission.rights.includes(
    "LOGISTIC_PROVIDER_LIST_FILTER"
  );

  const { id } = useParams<{ id: string }>();
  const customerID = decode(id) || "";

  const [tabActiveKey, setTabActiveKey] = useState(tabKey);
  const [forceRender, setForceRender] = useState<boolean>(false);

  const [filters, setFilters] = useState<{ [key: string]: string | boolean | undefined }>(
    initialFilters
  );
  const [sorting, setSorting] = useState<Sorting>(initialSorting);
  const [approvalStatus, setApprovalStatus] = useState<CustomerApprovalStatus>(
    CustomerApprovalStatus.APPROVED
  );
  const [list, setList] = useState<any>([]);
  const [count, setCount] = useState<number>(0);
  const [customerDetails, setCustomerDetails] = useState<any>({});
  const [customerStatus, setCustomerStatus] = useRecoilState<CustomerAtomType>(customerAtom);
  const [companyList, setCompanyList] = useState<OrgList[]>([]);
  const [logisticList, setLogisticList] = useState<OrgList[]>([]);
  const globalFilterValue = useRecoilValue(GlobalFilterAtom);
  const [selectedOrgIDs, setSelectedOrgIDs] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!customerID) {
      if (canViewList) {
        handleFetchCustomerList();
      } else {
        const orgID = encode(org._id);
        history.push(`/customer-management/profile/${orgID}/view`);
      }
      if (
        showLogisticProviderListFilter &&
        !isEmpty(filters) &&
        filters.logisticProviderOrgID !== ""
      ) {
        handleFetchCompanyList(filters.logisticProviderOrgID as string | undefined);
      }
    }
  }, [queryParams.offset, filters, sorting, approvalStatus, itemPerPage]);

  useEffect(() => {
    if (!customerID && canFetchCount) {
      handleFetchCustomerListCount();
    }
    if (showLogisticProviderListFilter) {
      handleFetchLogisticProviderList();
    } else {
      if (showCompanyListFilter) {
        handleFetchCompanyList(org._id);
      }
    }
  }, []);

  useEffect(() => {
    if (customerID && canView) {
      handleFetchCustomerDetails();
    }
  }, [customerID]);

  useEffect(() => {
    handleFetchCustomerList();
  }, [selectedOrgIDs]);

  useEffect(() => {
    let orgIDs: string[] = [];
    if (!isEmpty(globalFilterValue.companyIds)) {
      globalFilterValue?.companyIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
      setSelectedOrgIDs(orgIDs);
    } else if (!isEmpty(globalFilterValue.logisticsProviderIds)) {
      globalFilterValue?.logisticsProviderIds?.map((orgSelected) => {
        orgIDs.push(orgSelected);
      });
      setSelectedOrgIDs(orgIDs);
    } else {
      orgIDs.push(org._id);
      setSelectedOrgIDs(orgIDs);
    }
  }, [globalFilterValue]);

  useEffect(() => {
    switch (tabActiveKey) {
      case "0": {
        setApprovalStatus(CustomerApprovalStatus.APPROVED);
        break;
      }
      case "1": {
        setApprovalStatus(CustomerApprovalStatus.UNAPPROVED);
        break;
      }
      case "2": {
        setApprovalStatus(CustomerApprovalStatus.PENDING);
        break;
      }
      case "3": {
        setApprovalStatus(CustomerApprovalStatus.REJECTED);
        break;
      }
    }
  }, [tabActiveKey]);

  const handleTabClick = (key: string) => {
    let path = "";

    if (key === "0") {
      path = page ? `/customer-management/profile?page=${page}` : `/customer-management/profile`;
    } else {
      path = page
        ? `/customer-management/profile?tabKey=${key}&page=${page}`
        : `/customer-management/profile?tabKey=${key}`;
    }

    setTabActiveKey(key);
    setForceRender(true);
    history.push(path);
  };

  const handlePaginationChange = (page?: string) => {
    let path = "";

    if (page) {
      path = tabKey
        ? `/customer-management/profile?tabKey=${tabKey}&page=${page}`
        : `/customer-management/profile?page=${page}`;
    } else {
      path = tabKey
        ? `/customer-management/profile?tabKey=${tabKey}`
        : "/customer-management/profile";
    }
    history.push(path);
  };

  const handleCreateCustomerAction = () => {
    history.push("/customer-management/profile/create");
  };

  const handleSort = async (sortBy) => {
    if (sorting.sortBy === sortBy) {
      let mode = SortingMode.NEITHER;
      switch (sorting.mode) {
        case SortingMode.ASC: {
          mode = SortingMode.DESC;
          break;
        }
        case SortingMode.DESC: {
          mode = SortingMode.ASC;
          break;
        }
        case SortingMode.NEITHER: {
          mode = SortingMode.DESC;
          break;
        }
      }
      setSorting({ ...sorting, mode });
    } else {
      setSorting({ sortBy: sortBy, mode: SortingMode.DESC });
    }
  };

  const commonProps = {
    list,
    forceRender,
    sorting,
    isReadonly,
    showCompanyColumn,
    onPaginationChange: handlePaginationChange,
    onFilter(filter) {
      setFilters(filter);
    },
    onSort(sort: string) {
      handleSort(sort);
    },
    onViewDetails(orgID: string) {
      const id = encode(orgID);
      history.push(`/customer-management/profile/${id}/view`);
    },
    onSubmitApprovalStatus(data: CustomerApprovalInput) {
      handleRejectApproveCustomer(data);
    },
    onDeleteCustomer(orgID: string, tempDeletedReason: string) {
      handleDeleteCustomer(orgID, tempDeletedReason);
    },
    onFetchCustomerList() {
      handleFetchCustomerList();
    },
    onSubmitOperationalHours(operationalHours: any, orgIDs: any) {
      handleSubmitOperationalHoursForIndividuals(operationalHours, orgIDs);
    },
    logisticList,
    companyList,
    isLoading,
  };

  const returnCustomerProfileContent = (tabNumber: number) => {
    return (
      <React.Fragment>
        <CustomerProfileListComponent tab={tabNumber} {...commonProps} />
        <div className="my-30">{paginationComponent}</div>
      </React.Fragment>
    );
  };

  const tabs = [
    {
      tabKey: "0",
      label: t("tab.activeCustomer"),
      component: returnCustomerProfileContent(1),
    },
    {
      tabKey: "1",
      label: t("tab.unapprovedCustomer"),
      component: returnCustomerProfileContent(2),
    },
    {
      tabKey: "2",
      label: t("tab.pendingVerification"),
      component: returnCustomerProfileContent(3),
    },
    {
      tabKey: "3",
      label: t("tab.rejectedCustomer"),
      component: returnCustomerProfileContent(4),
    },
  ];

  const handleFetchCustomerList = async () => {
    //If there is no global filter, set it as own
    setIsLoading && setIsLoading(true);
    let orgIDs: string[] = [];
    orgIDs.push(org._id);

    const responseData = await fetchCustomerList(
      filters,
      !isEmpty(selectedOrgIDs) ? selectedOrgIDs : orgIDs,
      approvalStatus,
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      sorting
    );

    if (responseData && !responseData.error) {
      setList(responseData);
      setCurrentListCount(responseData.length);
      setIsLoading && setIsLoading(false);
    } else {
      toaster("error", responseData.error.message);
      setIsLoading && setIsLoading(false);
    }
  };

  const handleFetchCustomerListCount = async () => {
    //If there is no global filter, set it as own
    let orgIDs: string[] = [];
    orgIDs.push(org._id);
    const responseData = await fetchCustomerListCount(
      {},
      !isEmpty(selectedOrgIDs) ? selectedOrgIDs : orgIDs,
      CustomerApprovalStatus.UNAPPROVED
    );

    if (responseData && !responseData.error) {
      setCount(responseData.count);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleFetchCustomerDetails = async () => {
    setIsLoading && setIsLoading(true);
    const responseData = await fetchCustomer(customerID);

    if (responseData && !responseData.error) {
      setCustomerDetails(responseData);
      setIsLoading && setIsLoading(false);
    } else {
      toaster("error", responseData.error.message);
      setIsLoading && setIsLoading(false);
    }
  };

  const handleGenerateBearerToken = async () => {
    setIsLoading && setIsLoading(true);
    const responseData = await fetchBearerToken(customerID)

    if (responseData && !responseData.error) {
      toaster("info", t("txt.generatedToken", {field: responseData}))
      setIsLoading && setIsLoading(false);
    } else {
      toaster("error", responseData.error.message);
      setIsLoading && setIsLoading(false);
    }
  };

  const handleRejectApproveCustomer = async (data: CustomerApprovalInput) => {
    setCustomerStatus((prevStatus) => ({ ...prevStatus, reject: "loading" }));
    const responseData = await customerApproval(data);

    if (responseData && !responseData.error) {
      setCustomerStatus((prevStatus) => ({ ...prevStatus, reject: "success" }));
      toaster("success", responseData.message);
      handleFetchCustomerListCount();
    } else {
      setCustomerStatus((prevStatus) => ({ ...prevStatus, reject: "error" }));
      toaster("error", responseData.error.message);
      handleFetchCustomerListCount();
    }
  };

  const handleDeleteCustomer = async (orgID: string, tempDeletedReason: string) => {
    setCustomerStatus((prevStatus) => ({ ...prevStatus, delete: "loading" }));
    const responseData = await deleteCustomer(orgID, tempDeletedReason);

    if (responseData && !responseData.error) {
      setCustomerStatus((prevStatus) => ({ ...prevStatus, delete: "success" }));
      toaster("success", t("toaster.deleteCustomerSucess"));
    } else {
      setCustomerStatus((prevStatus) => ({ ...prevStatus, delete: "error" }));
      toaster("error", responseData.error.message);
    }
  };

  const handleRestoreCustomer = async (orgID: string) => {
    setCustomerStatus((prevStatus) => ({ ...prevStatus, restore: "loading" }));
    const responseData = await restoreCustomer(orgID);

    if (responseData && !responseData.error) {
      setCustomerStatus((prevStatus) => ({ ...prevStatus, restore: "success" }));
      toaster("success", t("toaster.restoreCustomerSucess"));
    } else {
      setCustomerStatus((prevStatus) => ({ ...prevStatus, restore: "error" }));
      toaster("error", responseData.error.message);
    }
  };

  const handleFetchCompanyList = async (orgID?: string) => {
    const responseData = await getCompanyList(orgID);
    if (responseData && !responseData.error) {
      setCompanyList(responseData);
    } else {
      if (typeof responseData.error.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }
  };

  const handleFetchLogisticProviderList = async () => {
    const responseData = await getLogisticProviderList();
    if (responseData && !responseData.error) {
      setLogisticList(responseData);
    } else {
      if (typeof responseData.error?.message === "string") {
        toaster("error", responseData.error.message);
      } else {
        toaster("error", responseData.error.message.message.replace('"', ""));
      }
    }
  };

  const handleSubmitOperationalHoursForIndividuals = async (operationalHours, orgIDs) => {
    const responseData = await updateOperationalHoursForIndividuals(orgIDs, operationalHours);
    if (responseData && !responseData.error) {
      toaster("success", "Updated Operational Hours for all individual customer");
    } else {
      toaster("error", "Error updating Operational Hours individual customers");
    }
  };

  return (
    <div className="customer-management customer-profile-page">
      <Helmet>
        <title>{t("pageTitle.customerProfile")}</title>
      </Helmet>
      <div className="title mb-5">{t("title.customerManagement")}</div>
      <div className="subtitle">{t("title.customerProfile")}</div>
      {customerID ? (
        <CustomerProfileDetailsComponent
          onBack={() => {
            history.goBack();
          }}
          customerDetails={customerDetails}
          onDeleteCustomer={handleDeleteCustomer}
          onRestoreCustomer={handleRestoreCustomer}
          onFetchCustomer={handleFetchCustomerDetails}
          canDelete={canDelete}
          canViewList={canViewList}
          canUpdate={canUpdate}
          handleGenerateBearerToken={handleGenerateBearerToken}
          isLoading={isLoading}
        />
      ) : (
        <React.Fragment>
          {isReadonly ? null : (
            <Row className="mt-20">
              <Col xs={24} sm={12}>
                <div
                  className="approval-notification"
                  onClick={() => {
                    tabActiveKey !== "1" ? handleTabClick("1") : {};
                  }}
                >
                  <div className="p-15 mr-5 txt-container">
                    <div className="count">{count}</div>
                    <div className="text mt-10">{t("txt.needApproval")}</div>
                  </div>
                  <div className="icon-container">
                    <img src={"/assets/icons/needApproval.svg"} alt="" width={30} height={45} />
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className="action-create-customer">
                  <Button
                    label={t("action.createCustomer")}
                    icon={<RightCircleOutlined />}
                    type="primary"
                    onClick={() => {
                      handleCreateCustomerAction();
                    }}
                  />
                </div>
              </Col>
            </Row>
          )}

          <Tabs
            destroyInactiveTabPane={true}
            onTabClick={handleTabClick}
            defaultActiveKey="0"
            activeKey={tabActiveKey}
            tabBarGutter={25}
            tabs={tabs}
            className={isReadonly ? "mt-20" : ""}
          />
        </React.Fragment>
      )}
    </div>
  );
};

export default CustomerProfilePage;
